import React, { useState } from 'react';
import { DeveloperData } from '../types';
import { isRemoteArtworkUrl } from '../services/iconCache';

export interface DeveloperAvatarProps {
  developer?: Partial<DeveloperData> | null;
  name?: string;
  avatarUrl?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  shape?: 'squircle' | 'circle' | 'rounded';
  className?: string;
}

const SIZE_MAP: Record<string, { sizeClass: string; textClass: string; px: number }> = {
  xs: { sizeClass: 'w-6 h-6', textClass: 'text-[10px] font-extrabold', px: 24 },
  sm: { sizeClass: 'w-8 h-8', textClass: 'text-xs font-black', px: 32 },
  md: { sizeClass: 'w-12 h-12', textClass: 'text-base font-black', px: 48 },
  lg: { sizeClass: 'w-16 h-16', textClass: 'text-xl font-black', px: 64 },
  xl: { sizeClass: 'w-20 h-20', textClass: 'text-2xl font-black', px: 80 }
};

const DEV_BRAND_GRADIENTS: Record<string, string> = {
  'duolingo, inc.': 'from-[#58CC02] to-[#2E8B00]',
  'duolingo': 'from-[#58CC02] to-[#2E8B00]',
  'openai': 'from-[#10A37F] to-[#0A6B53]',
  'spotify ab': 'from-[#1ED760] to-[#128C3E]',
  'spotify': 'from-[#1ED760] to-[#128C3E]',
  'bytedance ltd.': 'from-[#0F172A] to-[#FE2C55]',
  'bytedance': 'from-[#0F172A] to-[#FE2C55]',
  'meta platforms, inc.': 'from-[#0081FB] to-[#0064E0]',
  'meta': 'from-[#0081FB] to-[#0064E0]',
  'notion labs, inc.': 'from-[#2F3437] to-[#111827]',
  'notion': 'from-[#2F3437] to-[#111827]',
  'canva pty ltd': 'from-[#00C4CC] to-[#7D2AE8]',
  'canva': 'from-[#00C4CC] to-[#7D2AE8]'
};

export const DeveloperAvatar: React.FC<DeveloperAvatarProps> = ({
  developer,
  name,
  avatarUrl,
  size = 'md',
  shape = 'squircle',
  className = ''
}) => {
  const devName = name || developer?.name || 'Developer';
  const cleanName = devName.toLowerCase().trim();

  const candidateSrc = avatarUrl || developer?.avatarUrl || null;
  const validRemoteSrc = candidateSrc && isRemoteArtworkUrl(candidateSrc) ? candidateSrc : null;

  const [hasFailed, setHasFailed] = useState(!validRemoteSrc);
  const [isLoaded, setIsLoaded] = useState(false);

  const sizeConfig = typeof size === 'string' && SIZE_MAP[size] ? SIZE_MAP[size] : SIZE_MAP.md;
  const customStyle = typeof size === 'number' ? { width: `${size}px`, height: `${size}px` } : undefined;

  let roundedClass = 'rounded-2xl';
  if (shape === 'circle') roundedClass = 'rounded-full';
  else if (shape === 'rounded') roundedClass = 'rounded-xl';

  // Find custom or deterministic gradient for developer monogram
  let gradient = 'from-slate-700 to-slate-900';
  for (const [key, grad] of Object.entries(DEV_BRAND_GRADIENTS)) {
    if (cleanName.includes(key)) {
      gradient = grad;
      break;
    }
  }

  const initial = (devName.trim().charAt(0) || 'D').toUpperCase();

  return (
    <div
      className={`relative inline-flex items-center justify-center flex-shrink-0 select-none overflow-hidden ${roundedClass} ${
        typeof size === 'string' ? sizeConfig.sizeClass : ''
      } ${className}`}
      style={customStyle}
    >
      {/* 1. Base Layer: Developer Fallback Monogram */}
      <div
        style={{ zIndex: 1 }}
        className={`absolute inset-0 w-full h-full flex items-center justify-center bg-gradient-to-br ${gradient} text-white shadow-inner border border-white/20 select-none ${roundedClass}`}
        title={devName}
      >
        <span className={`${sizeConfig.textClass} leading-none tracking-tight`}>{initial}</span>
      </div>

      {/* 2. Top Layer: Verified Developer Avatar / Logo */}
      {!hasFailed && validRemoteSrc && (
        <img
          src={validRemoteSrc}
          alt={`${devName} avatar`}
          loading="eager"
          crossOrigin="anonymous"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 2
          }}
          className={`transition-opacity duration-150 ${isLoaded ? 'opacity-100' : 'opacity-0'} ${roundedClass}`}
          onLoad={(e) => {
            if (e.currentTarget.naturalWidth > 0) {
              setIsLoaded(true);
            }
          }}
          onError={() => {
            setHasFailed(true);
            setIsLoaded(false);
          }}
        />
      )}
    </div>
  );
};

export default DeveloperAvatar;
